import { useIntl as useSharedIntl } from '@docue/utils/useIntl'

export default () => {
  const { t } = useI18n()
  const { language } = useLanguage()
  const { workspace } = useAuth()

  const locale = computed(() => {
    if (!workspace.value)
      return language.value

    return new Intl.Locale(language.value, {
      region: workspace.value.attributes.country,
    }).toString()
  })

  const {
    formatDate,
    formatDateTime,
    formatRelativeTime,
    getLanguageName,
    getRegionName: getBaseRegionName,
  } = useSharedIntl(locale)

  const isLoadingConfigEnabled = computed(() => !!workspace.value)
  const { data: config } = useQueryConfig.current({
    useQueryOptions: {
      enabled: isLoadingConfigEnabled,
    },
  })

  /**
   * Formats given number to currency string based on
   * current language and workspace's country.
   *
   * @throws {TypeError}  If no country configuration available
   */
  const formatCurrency = (
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ): string => new Intl.NumberFormat(locale.value, {
    ...options,
    style: 'currency',
    currency: config.value?.currency_code || 'EUR',
  }).format(amount)

  /**
   *
   * Formats given number to currency string based on
   * current language and workspace's country.
   *
   * If the amount is not an integer, it will be formatted
   * with 2 decimal places.
   *
   * @throws {TypeError}  If no country configuration available
   */
  const formatCurrencySmart = (
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style' | 'currency'>,
  ): string => formatCurrency(amount, {
    ...options,
    minimumFractionDigits: amount % 1 !== 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })

  /**
   * Formats given number to percentage string based on current language
   */
  const formatPercent = (
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style'>,
  ) => new Intl.NumberFormat(locale.value, {
    ...options,
    style: 'percent',
  }).format(amount)

  const getRegionName = (code: string) => {
    if (code.includes('-')) {
      switch (code) {
        case 'GB-EAW': return t('legislation.GB-EAW')
        case 'GB-ENG': return t('legislation.GB-ENG')
        case 'GB-NIR': return t('legislation.GB-NIR')
        case 'GB-SCT': return t('legislation.GB-SCT')
        case 'GB-WLS': return t('legislation.GB-WLS')
      }
    }

    return getBaseRegionName(code)
  }

  /**
   * sorts the given items by making use of Intl.Collator().compare
   * @param items the items to sort
   * @param getSortCriterionFromItemFunction
   *   a function that returns the sort criterion from each item in items
   * @param collatorOptions the options passed to the constructor of Intl.Collarotor
   */
  const sortIntl = <T>(
    items: Readonly<T[]>,
    getSortCriterionFromItemFunction = (item: T): string => item as unknown as string,
    collatorOptions: Intl.CollatorOptions | undefined = undefined,
  ) =>
    [...items].sort((a, b) =>
      Intl.Collator(language.value, collatorOptions).compare(
        getSortCriterionFromItemFunction(a) || '',
        getSortCriterionFromItemFunction(b) || '',
      ),
    )

  return {
    locale,
    sortIntl,
    formatDate,
    getRegionName,
    formatPercent,
    formatCurrency,
    formatCurrencySmart,
    formatDateTime,
    formatRelativeTime,
    displayLanguageName: (code: string) => getLanguageName(code, 'short'),
  }
}
